import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiSave, 
  FiArrowLeft, 
  FiArrowRight, 
  FiX, 
  FiChevronDown, 
  FiCheck, 
  FiPlus, 
  FiTrash2, 
  FiEdit2, 
  FiImage,
  FiLoader 
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { Helmet } from 'react-helmet-async';
import api from '../../../api/api';
import { HexColorPicker } from 'react-colorful';

const STEPS = [
  { id: 1, title: 'Basic Details' },
  { id: 2, title: 'Variants' },
  { id: 3, title: 'Images' },
  { id: 4, title: 'Additional Info' }
];

const MultiSelect = ({ options, value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredOptions = options.filter(option =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggleOption = (tagId) => {
    const newValue = value.includes(tagId)
      ? value.filter(id => id !== tagId)
      : [...value, tagId];
    onChange(newValue);
  };

  return (
    <div className="relative">
      <div
        className="min-h-[42px] px-3 py-2 border border-gray-300 rounded-md shadow-sm cursor-pointer bg-white"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex flex-wrap gap-2">
          {value.length > 0 ? (
            options
              .filter(option => value.includes(option.id))
              .map(option => (
                <span
                  key={option.id}
                  className="inline-flex items-center px-2 py-1 rounded-md text-sm bg-indigo-100 text-indigo-800"
                >
                  {option.name}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleOption(option.id);
                    }}
                    className="ml-1 hover:text-indigo-500"
                  >
                    <FiX className="h-4 w-4" />
                  </button>
                </span>
              ))
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </div>
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <FiChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
          <div className="p-2">
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Search tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <ul className="max-h-60 overflow-auto py-1">
            {filteredOptions.map(option => (
              <li
                key={option.id}
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleOption(option.id);
                }}
              >
                <span>{option.name}</span>
                {value.includes(option.id) && (
                  <FiCheck className="h-5 w-5 text-indigo-600" />
                )}
              </li>
            ))}
            {filteredOptions.length === 0 && (
              <li className="px-3 py-2 text-gray-500">No tags found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const ColorPicker = ({ color, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <div
          className="w-10 h-10 rounded-md cursor-pointer border shadow-sm"
          style={{ backgroundColor: color }}
          onClick={() => setIsOpen(!isOpen)}
        />
        <input
          type="text"
          value={color}
          onChange={(e) => {
            const newColor = e.target.value;
            if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(newColor)) {
              onChange(newColor);
            }
          }}
          className="w-24 px-2 py-1 border border-gray-300 rounded-md text-sm"
          placeholder="#000000"
        />
      </div>
      
      {isOpen && (
        <div className="absolute z-10 top-12">
          <div className="fixed inset-0" onClick={() => setIsOpen(false)} />
          <div className="relative">
            <HexColorPicker color={color} onChange={onChange} />
          </div>
        </div>
      )}
    </div>
  );
};

const VariantForm = ({ variant, onChange, onDelete, index }) => {
  const generateSKU = (size, color) => {
    const prefix = 'DYX';
    const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const sizeCode = size.replace(/\s+/g, '').substring(0, 2).toUpperCase();
    const colorCode = color.replace(/\s+/g, '').substring(0, 2).toUpperCase();
    return `${prefix}${sizeCode}${colorCode}${randomNum}`;
  };

  const handleChange = (field, value) => {
    const updatedVariant = { ...variant, [field]: value };
    
    // Auto-generate SKU when both size and color are set
    if (field === 'size' || field === 'color') {
      if (updatedVariant.size && updatedVariant.color) {
        updatedVariant.sku = generateSKU(updatedVariant.size, updatedVariant.color);
      }
    }
    
    onChange(index, updatedVariant);
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 mb-4">
      <div className="flex justify-between items-center mb-4">
        <h4 className="text-lg font-medium">Variant {index + 1}</h4>
        <button
          type="button"
          onClick={() => onDelete(index)}
          className="text-red-500 hover:text-red-700"
        >
          <FiTrash2 className="w-5 h-5" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Size */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Size <span className="text-red-500">*</span>
          </label>
          <select
            value={variant.size}
            onChange={(e) => handleChange('size', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            required
          >
            <option value="">Select Size</option>
            <option value="Free Size">Free Size</option>
            <option value="XXS">XXS</option>
            <option value="XS">XS</option>
            <option value="S">S</option>
            <option value="M">M</option>
            <option value="L">L</option>
            <option value="XL">XL</option>
            <option value="XXL">XXL</option>
            <option value="XXXL">XXXL</option>
          </select>
        </div>

        {/* Color Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Color Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={variant.color}
            onChange={(e) => handleChange('color', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder="e.g., Royal Blue"
            required
          />
        </div>

        {/* Color Picker */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Color Code <span className="text-red-500">*</span>
          </label>
          <ColorPicker
            color={variant.color_code}
            onChange={(color) => handleChange('color_code', color)}
          />
        </div>

        {/* Stock */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Stock <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            value={variant.stock}
            onChange={(e) => handleChange('stock', parseInt(e.target.value) || 0)}
            min="0"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            required
          />
        </div>

        {/* SKU */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            SKU <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={variant.sku}
            onChange={(e) => handleChange('sku', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder="Auto-generated SKU"
            required
          />
        </div>
      </div>
    </div>
  );
};

const VariantList = ({ variants }) => {
  if (!variants.length) {
    return <p className="text-gray-500 text-center py-4">No variants added yet</p>;
  }

  return (
    <div className="mt-6">
      <h3 className="text-lg font-medium mb-4">Added Variants</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Color</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Color Preview</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {variants.map((variant, index) => (
              <tr key={variant.id || index}>
                <td className="px-6 py-4 whitespace-nowrap">{variant.size}</td>
                <td className="px-6 py-4 whitespace-nowrap">{variant.color}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div 
                    className="w-8 h-8 rounded-md border shadow-sm" 
                    style={{ backgroundColor: variant.color_code }}
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{variant.stock}</td>
                <td className="px-6 py-4 whitespace-nowrap font-mono">{variant.sku}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const VariantImageManager = ({ variants, productId, onImagesChange }) => {
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [variantImages, setVariantImages] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Initialize images for existing variants
  useEffect(() => {
    const initialImages = {};
    variants.forEach(variant => {
      if (variant.id) {
        initialImages[variant.id] = variant.images || [];
      }
    });
    setVariantImages(initialImages);
  }, [variants]);

  const handleVariantSelect = (variant) => {
    if (!variant.id) {
      toast.error('Invalid variant selected');
      return;
    }
    setSelectedVariant(variant);
    if (!variantImages[variant.id]) {
      setVariantImages(prev => ({
        ...prev,
        [variant.id]: []
      }));
    }
  };

  const validateImage = (file) => {
    // Max size: 5MB
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error('Image size should be less than 5MB');
    }

    // Allowed types
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Only JPG, PNG and WebP images are allowed');
    }

    return true;
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!selectedVariant?.id || !files.length) return;

    setIsUploading(true);

    try {
      // Validate all files first
      const validFiles = files.filter(file => {
        try {
          validateImage(file);
          return true;
        } catch (error) {
          toast.error(`${file.name}: ${error.message}`);
          return false;
        }
      });

      if (!validFiles.length) {
        throw new Error('No valid images to upload');
      }

      // Create a single FormData containing all valid files
      const formData = new FormData();
      validFiles.forEach((file, index) => {
        formData.append('images', file);
      });

      // Single API call with all images
      const response = await api.uploadVariantImage(
        productId,
        selectedVariant.id,
        formData
      );

      // Handle array response
      const newImages = Array.isArray(response) ? response : [response];

      // Update the UI with all new images
      setVariantImages(prev => ({
        ...prev,
        [selectedVariant.id]: [
          ...(prev[selectedVariant.id] || []),
          ...newImages.map(img => ({
            id: img.id,
            url: img.images,
            uniqueKey: `${Date.now()}_${Math.random()}`,
            timestamp: Date.now()
          }))
        ]
      }));

      // Notify parent component
      if (typeof onImagesChange === 'function') {
        newImages.forEach(img => {
          onImagesChange(selectedVariant.id, {
            id: img.id,
            url: img.images
          });
        });
      }

      toast.success('Images uploaded successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to upload images');
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeImage = async (variantId, imageId, index) => {
    if (!variantId || !imageId) return;

    try {
      await api.deleteVariantImage(productId, variantId, imageId);

      setVariantImages(prev => ({
        ...prev,
        [variantId]: prev[variantId].filter((_, i) => i !== index)
      }));

      toast.success('Image removed successfully');
    } catch (error) {
      toast.error(`Failed to remove image: ${error.message}`);
      console.error('Remove image error:', error);
    }
  };

  return (
    <div className="mt-8 space-y-6">
      <h3 className="text-lg font-medium">Variant Images</h3>
      
      {/* Variant Selection Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {variants.map((variant) => (
          <button
            key={variant.id}
            onClick={() => handleVariantSelect(variant)}
            className={`
              p-4 rounded-lg border-2 transition-all
              ${selectedVariant?.id === variant.id 
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-gray-200 hover:border-indigo-200'
              }
            `}
          >
            <div 
              className="w-full h-20 rounded-md mb-2"
              style={{ backgroundColor: variant.color_code }}
            />
            <div className="text-sm font-medium truncate">{variant.color}</div>
            <div className="text-xs text-gray-500">{variant.size}</div>
            <div className="text-xs text-gray-400 mt-1">
              {`${variantImages[variant.id]?.length || 0} images`}
            </div>
          </button>
        ))}
      </div>

      {/* Image Upload Section */}
      {selectedVariant && (
        <div className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-md font-medium">
              Images for {selectedVariant.color} - {selectedVariant.size}
            </h4>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className={`
                inline-flex items-center px-4 py-2 border border-transparent 
                rounded-md shadow-sm text-sm font-medium text-white
                ${isUploading 
                  ? 'bg-indigo-400 cursor-not-allowed' 
                  : 'bg-indigo-600 hover:bg-indigo-700'
                }
              `}
            >
              {isUploading ? (
                <>
                  <FiLoader className="animate-spin mr-2 -ml-1 h-5 w-5" />
                  Uploading...
                </>
              ) : (
                <>
                  <FiPlus className="mr-2 -ml-1 h-5 w-5" />
                  Add Images
                </>
              )}
            </button>
          </div>

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageUpload}
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
          />

          {/* Image Preview Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <AnimatePresence>
              {variantImages[selectedVariant.id]?.map((image, index) => (
                <motion.div
                  key={`${image.uniqueKey || image.id}_${index}`} // Use uniqueKey or fallback to combination of id and index
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="relative aspect-square"
                >
                  <img
                    src={image.url}
                    alt={`Variant ${index + 1}`}
                    className="w-full h-full object-cover rounded-lg"
                  />
                </motion.div>
              ))}
            </AnimatePresence>

            {(!variantImages[selectedVariant.id] || 
              variantImages[selectedVariant.id].length === 0) && (
              <div 
                onClick={() => !isUploading && fileInputRef.current?.click()}
                className={`
                  aspect-square border-2 border-dashed border-gray-300 
                  rounded-lg flex items-center justify-center cursor-pointer 
                  ${isUploading ? 'cursor-not-allowed' : 'hover:border-indigo-300'}
                  transition-colors
                `}
              >
                <div className="text-center">
                  <FiImage className="w-8 h-8 mx-auto text-gray-400" />
                  <span className="mt-2 block text-sm text-gray-500">
                    Add images
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CreateProductWizard = () => {
  const navigate = useNavigate();
  
  // Initialize states from localStorage if available
  const [currentStep, setCurrentStep] = useState(() => {
    const savedStep = localStorage.getItem('createProductStep');
    return savedStep ? parseInt(savedStep) : 1;
  });

  const [productId, setProductId] = useState(() => {
    const savedProductId = localStorage.getItem('createProductId');
    return savedProductId || null;
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Save step and productId to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('createProductStep', currentStep);
  }, [currentStep]);

  useEffect(() => {
    if (productId) {
      localStorage.setItem('createProductId', productId);
    }
  }, [productId]);

  // Clear localStorage when component unmounts or user completes/cancels
  useEffect(() => {
    return () => {
      localStorage.removeItem('createProductStep');
      localStorage.removeItem('createProductId');
    };
  }, []);

  // Form states
  const [basicDetails, setBasicDetails] = useState({
    name: '',
    category_id: '',
    brand_id: '',
    tag_ids: [],
    price: '',
    sale_price: '',
    description: '',
    production_time: '',
    shipping: '',
    returns_policy: '',
    is_new_arrival: false,
    is_featured: false
  });

  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch categories, brands, and tags on component mount
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [categoriesData, brandsData, tagsData] = await Promise.all([
          api.getCategories(),
          api.getBrands(),
          api.getTags()
        ]);

        // Filter active categories and brands
        const activeCategories = Array.isArray(categoriesData) 
          ? categoriesData.filter(cat => cat.is_active)
          : (categoriesData?.results || []).filter(cat => cat.is_active);

        const activeBrands = Array.isArray(brandsData)
          ? brandsData.filter(brand => brand.is_active)
          : (brandsData?.results || []).filter(brand => brand.is_active);

        setCategories(activeCategories);
        setBrands(activeBrands);
        setTags(tagsData);
      } catch (error) {
        console.error('Error fetching taxonomy data:', error);
        toast.error('Failed to load categories and brands');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const [variants, setVariants] = useState([]);

  const [currentVariant, setCurrentVariant] = useState({
    size: '',
    color: '',
    color_code: '#000000',
    stock: 0,
    sku: ''
  });

  const handleVariantSubmit = async (variant) => {
    try {
      if (!productId) {
        toast.error('Product ID is not available');
        return;
      }

      const variantData = {
        size: variant.size,
        color: variant.color,
        color_code: variant.color_code,
        stock: parseInt(variant.stock),
        sku: variant.sku
      };

      const response = await api.addProductVariant(productId, variantData);
      // Remove the success toast from here
      return response;
    } catch (error) {
      toast.error(error.message || 'Failed to add variant');
      console.error('Error adding variant:', error);
      throw error;
    }
  };

  const handleAddSingleVariant = async () => {
    if (!currentVariant.size || !currentVariant.color || !currentVariant.stock) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const savedVariant = await handleVariantSubmit(currentVariant);
      
      const newVariant = {
        ...currentVariant,
        id: savedVariant.id
      };
      
      setVariants([...variants, newVariant]);
      
      // Reset the form
      setCurrentVariant({
        size: '',
        color: '',
        color_code: '#000000',
        stock: 0,
        sku: ''
      });
      
      // Keep only this success toast
      toast.success('Variant added successfully');
    } catch (error) {
      // Error is already handled in handleVariantSubmit
      console.error('Failed to add variant:', error);
    }
  };

  const handleVariantChange = (index, updatedVariant) => {
    const newVariants = [...variants];
    newVariants[index] = updatedVariant;
    setVariants(newVariants);
  };

  const handleDeleteVariant = (index) => {
    setVariants(variants.filter((_, i) => i !== index));
  };

  const [variantImages, setVariantImages] = useState({});

  const handleVariantImagesChange = (sku, newImages) => {
    setVariantImages(prev => ({
      ...prev,
      [sku]: [...(prev[sku] || []), ...newImages]
    }));
  };

  const [additionalInfo, setAdditionalInfo] = useState({
    highlights: [],
    specifications: [],
    styling_tips: []
  });

  // Add these new states for additional info section
  const [currentHighlight, setCurrentHighlight] = useState('');
  const [currentSpecification, setCurrentSpecification] = useState('');
  const [currentStylingTip, setCurrentStylingTip] = useState('');
  const [isSubmittingAdditionalInfo, setIsSubmittingAdditionalInfo] = useState(false);

  // Add these handlers at component level
  const handleAddHighlight = async () => {
    if (!currentHighlight.trim()) {
      toast.error('Please enter a highlight');
      return;
    }

    setIsSubmittingAdditionalInfo(true);
    try {
      // Pass the highlight text directly
      await api.createProductHighlight(productId, currentHighlight);
      
      // Update local state after successful API call
      setAdditionalInfo(prev => ({
        ...prev,
        highlights: [...prev.highlights, currentHighlight]
      }));
      
      toast.success('Highlight added successfully');
      setCurrentHighlight('');
      
    } catch (error) {
      toast.error(error.message || 'Failed to add highlight');
    } finally {
      setIsSubmittingAdditionalInfo(false);
    }
  };

  const handleAddSpecification = async () => {
    if (!currentSpecification.trim()) {
      toast.error('Please enter a specification');
      return;
    }

    setIsSubmittingAdditionalInfo(true);
    try {
      await api.createProductSpecification(productId, {
        specification: currentSpecification
      });
      
      // Update local state after successful API call
      setAdditionalInfo(prev => ({
        ...prev,
        specifications: [...prev.specifications, currentSpecification]
      }));
      
      toast.success('Specification added successfully');
      setCurrentSpecification('');
      
    } catch (error) {
      toast.error(error.message || 'Failed to add specification');
    } finally {
      setIsSubmittingAdditionalInfo(false);
    }
  };

  const handleAddStylingTip = async () => {
    if (!currentStylingTip.trim()) {
      toast.error('Please enter a styling tip');
      return;
    }

    setIsSubmittingAdditionalInfo(true);
    try {
      await api.createProductStylingTips(productId, {
        styling_tips: currentStylingTip
      });
      
      // Update local state after successful API call
      setAdditionalInfo(prev => ({
        ...prev,
        styling_tips: [...prev.styling_tips, currentStylingTip]
      }));
      
      toast.success('Styling tip added successfully');
      setCurrentStylingTip('');
      
    } catch (error) {
      toast.error(error.message || 'Failed to add styling tip');
    } finally {
      setIsSubmittingAdditionalInfo(false);
    }
  };

  // Add handlers for deleting items
  const handleDeleteHighlight = async (index) => {
    try {
      // Assuming you have an API endpoint for deletion
      await api.deleteProductHighlight(productId, additionalInfo.highlights[index]);
      
      setAdditionalInfo(prev => ({
        ...prev,
        highlights: prev.highlights.filter((_, i) => i !== index)
      }));
      
      toast.success('Highlight deleted successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to delete highlight');
    }
  };

  const handleDeleteSpecification = async (index) => {
    try {
      await api.deleteProductSpecification(productId, additionalInfo.specifications[index]);
      
      setAdditionalInfo(prev => ({
        ...prev,
        specifications: prev.specifications.filter((_, i) => i !== index)
      }));
      
      toast.success('Specification deleted successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to delete specification');
    }
  };

  const handleDeleteStylingTip = async (index) => {
    try {
      await api.deleteProductStylingTip(productId, additionalInfo.styling_tips[index]);
      
      setAdditionalInfo(prev => ({
        ...prev,
        styling_tips: prev.styling_tips.filter((_, i) => i !== index)
      }));
      
      toast.success('Styling tip deleted successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to delete styling tip');
    }
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      if (!basicDetails.name || !basicDetails.category_id || !basicDetails.brand_id || !basicDetails.price) {
        toast.error('Please fill in all required fields');
        return;
      }

      setIsSubmitting(true);
      try {
        const productData = {
          ...basicDetails,
          tag_ids: basicDetails.tag_ids || []
        };

        // Show loading toast
        const loadingToast = toast.loading('Creating product...');

        const response = await api.createProduct(productData);
        toast.dismiss(loadingToast);
        
        setProductId(response.id);
        toast.success('Product created successfully');
        const nextStep = Math.min(currentStep + 1, STEPS.length);
        setCurrentStep(nextStep);
      } catch (error) {
        console.error('Error creating product:', error);
        toast.error(error.message || 'Failed to create product. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      const nextStep = Math.min(currentStep + 1, STEPS.length);
      setCurrentStep(nextStep);
    }
  };

  const handleBack = () => {
    const prevStep = Math.max(currentStep - 1, 1);
    setCurrentStep(prevStep);
  };

  const handleComplete = () => {
    // Clear localStorage when wizard is complete
    localStorage.removeItem('createProductStep');
    localStorage.removeItem('createProductId');
    navigate('/admin/products');
  };

  const handleCancel = () => {
    // Show confirmation dialog
    if (window.confirm('Are you sure you want to cancel? All progress will be lost.')) {
      localStorage.removeItem('createProductStep');
      localStorage.removeItem('createProductId');
      navigate('/admin/products');
    }
  };

  const renderBasicDetails = () => (
    <div className="space-y-8">
      {/* Main Product Information */}
      <section>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Main Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {/* Product Name */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Product Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={basicDetails.name}
              onChange={(e) => setBasicDetails({ ...basicDetails, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Enter product name"
              required
            />
          </div>

          {/* Category and Brand side by side */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              {isLoading ? (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                  Loading categories...
                </div>
              ) : (
                <select
                  value={basicDetails.category_id}
                  onChange={(e) => setBasicDetails({ ...basicDetails, category_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Brand <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              {isLoading ? (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                  Loading brands...
                </div>
              ) : (
                <select
                  value={basicDetails.brand_id}
                  onChange={(e) => setBasicDetails({ ...basicDetails, brand_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Brand</option>
                  {brands.map(brand => (
                    <option key={brand.id} value={brand.id}>{brand.name}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Tags */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags
            </label>
            <div className="relative">
              {isLoading ? (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                  Loading tags...
                </div>
              ) : (
                <MultiSelect
                  options={tags}
                  value={basicDetails.tag_ids}
                  onChange={(newTags) => setBasicDetails({ ...basicDetails, tag_ids: newTags })}
                  placeholder="Select tags..."
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Pricing</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Regular Price <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-500">₹</span>
              <input
                type="number"
                value={basicDetails.price}
                onChange={(e) => setBasicDetails({ ...basicDetails, price: e.target.value })}
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="0.00"
                min="0"
                step="0.01"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sale Price
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-500">₹</span>
              <input
                type="number"
                value={basicDetails.sale_price}
                onChange={(e) => setBasicDetails({ ...basicDetails, sale_price: e.target.value })}
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Description</h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Product Description <span className="text-red-500">*</span>
          </label>
          <textarea
            value={basicDetails.description}
            onChange={(e) => setBasicDetails({ ...basicDetails, description: e.target.value })}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder="Enter detailed product description"
            required
          />
        </div>
      </section>

      {/* Delivery Information */}
      <section>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Delivery Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Production Time
            </label>
            <input
              type="text"
              value={basicDetails.production_time}
              onChange={(e) => setBasicDetails({ ...basicDetails, production_time: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="e.g., 4-5 days"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Shipping Information
            </label>
            <input
              type="text"
              value={basicDetails.shipping}
              onChange={(e) => setBasicDetails({ ...basicDetails, shipping: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="e.g., Free shipping across India"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Returns Policy
            </label>
            <input
              type="text"
              value={basicDetails.returns_policy}
              onChange={(e) => setBasicDetails({ ...basicDetails, returns_policy: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="e.g., 7 days return policy"
            />
          </div>
        </div>
      </section>

      {/* Product Status */}
      <section>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Product Status</h3>
        <div className="flex space-x-6">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={basicDetails.is_new_arrival}
              onChange={(e) => setBasicDetails({ ...basicDetails, is_new_arrival: e.target.checked })}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-700">Mark as New Arrival</span>
          </label>

          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={basicDetails.is_featured}
              onChange={(e) => setBasicDetails({ ...basicDetails, is_featured: e.target.checked })}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-700">Mark as Featured</span>
          </label>
        </div>
      </section>
    </div>
  );

  const renderVariants = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Add New Variant</h2>
      </div>

      {/* Single Variant Form */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Size <span className="text-red-500">*</span>
            </label>
            <select
              value={currentVariant.size}
              onChange={(e) => {
                const newSize = e.target.value;
                const newSku = newSize && currentVariant.color 
                  ? generateSKU(newSize, currentVariant.color)
                  : '';
                setCurrentVariant({ 
                  ...currentVariant, 
                  size: newSize,
                  sku: newSku
                });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            >
            <option value="">Select Size</option>
            <option value="Free Size">Free Size</option>
            <option value="XXS">XXS</option>
            <option value="XS">XS</option>
            <option value="S">S</option>
            <option value="M">M</option>
            <option value="L">L</option>
            <option value="XL">XL</option>
            <option value="XXL">XXL</option>
            <option value="XXXL">XXXL</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Color Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={currentVariant.color}
              onChange={(e) => {
                const newColor = e.target.value;
                const newSku = newColor && currentVariant.size 
                  ? generateSKU(currentVariant.size, newColor)
                  : '';
                setCurrentVariant({ 
                  ...currentVariant, 
                  color: newColor,
                  sku: newSku
                });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="e.g., Royal Blue"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Color Code <span className="text-red-500">*</span>
            </label>
            <input
              type="color"
              value={currentVariant.color_code}
              onChange={(e) => setCurrentVariant({ ...currentVariant, color_code: e.target.value })}
              className="w-full h-10 px-1 py-1 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Stock <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={currentVariant.stock}
              onChange={(e) => setCurrentVariant({ ...currentVariant, stock: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Enter stock quantity"
              min="0"
              required
            />
          </div>

          {/* Add SKU field */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SKU <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={currentVariant.sku}
                onChange={(e) => setCurrentVariant({ ...currentVariant, sku: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter SKU or let it auto-generate"
                required
              />
              <button
                type="button"
                onClick={() => {
                  if (currentVariant.size && currentVariant.color) {
                    const newSku = generateSKU(currentVariant.size, currentVariant.color);
                    setCurrentVariant({ ...currentVariant, sku: newSku });
                  } else {
                    toast.error('Both size and color are required to generate SKU');
                  }
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Generate
              </button>
            </div>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={handleAddSingleVariant}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <FiPlus className="mr-2 -ml-1 h-5 w-5" />
            Add Variant
          </button>
        </div>
      </div>

      {/* Added Variants List */}
      <VariantList variants={variants} />
    </div>
  );

  const renderAdditionalInfo = () => {
    return (
      <div className="space-y-8">
        {/* Highlights Section */}
        <section className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Product Highlights</h3>
            <span className="text-sm text-gray-500">{additionalInfo.highlights.length} items</span>
          </div>
          <div className="space-y-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={currentHighlight}
                onChange={(e) => setCurrentHighlight(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter product highlight"
              />
              <button
                type="button"
                onClick={handleAddHighlight}
                disabled={isSubmittingAdditionalInfo}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                Add
              </button>
            </div>
            {/* Display Added Highlights */}
            <div className="mt-4 bg-gray-50 p-4 rounded-lg">
              {additionalInfo.highlights.length === 0 ? (
                <p className="text-gray-500 text-sm text-center">No highlights added yet</p>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {additionalInfo.highlights.map((highlight, index) => (
                    <li key={index} className="py-3 flex items-center">
                      <div className="flex items-center space-x-2">
                        <span className="w-2 h-2 bg-indigo-600 rounded-full"></span>
                        <span className="text-sm text-gray-700">{highlight}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>

        {/* Specifications Section */}
        <section className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Product Specifications</h3>
            <span className="text-sm text-gray-500">{additionalInfo.specifications.length} items</span>
          </div>
          <div className="space-y-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={currentSpecification}
                onChange={(e) => setCurrentSpecification(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter product specification"
              />
              <button
                type="button"
                onClick={handleAddSpecification}
                disabled={isSubmittingAdditionalInfo}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                Add
              </button>
            </div>
            {/* Display Added Specifications */}
            <div className="mt-4 bg-gray-50 p-4 rounded-lg">
              {additionalInfo.specifications.length === 0 ? (
                <p className="text-gray-500 text-sm text-center">No specifications added yet</p>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {additionalInfo.specifications.map((spec, index) => (
                    <li key={index} className="py-3 flex items-center">
                      <div className="flex items-center space-x-2">
                        <span className="w-2 h-2 bg-indigo-600 rounded-full"></span>
                        <span className="text-sm text-gray-700">{spec}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>

        {/* Styling Tips Section */}
        <section className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Styling Tips</h3>
            <span className="text-sm text-gray-500">{additionalInfo.styling_tips.length} items</span>
          </div>
          <div className="space-y-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={currentStylingTip}
                onChange={(e) => setCurrentStylingTip(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter styling tip"
              />
              <button
                type="button"
                onClick={handleAddStylingTip}
                disabled={isSubmittingAdditionalInfo}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                Add
              </button>
            </div>
            {/* Display Added Styling Tips */}
            <div className="mt-4 bg-gray-50 p-4 rounded-lg">
              {additionalInfo.styling_tips.length === 0 ? (
                <p className="text-gray-500 text-sm text-center">No styling tips added yet</p>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {additionalInfo.styling_tips.map((tip, index) => (
                    <li key={index} className="py-3 flex items-center">
                      <div className="flex items-center space-x-2">
                        <span className="w-2 h-2 bg-indigo-600 rounded-full"></span>
                        <span className="text-sm text-gray-700">{tip}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderBasicDetails();
      case 2:
        return renderVariants();
      case 3:
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">Variant Images</h2>
            {variants.length > 0 && (
              <VariantImageManager 
                variants={variants}
                productId={productId}
                onImagesChange={(variantId, imageData) => {
                  // Handle image change if needed
                  console.log('Image uploaded for variant:', variantId, imageData);
                }}
              />
            )}
          </div>
        );
      case 4:
        return renderAdditionalInfo();
      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Add New Product | Admin | Panchali Boutique</title>
      </Helmet>
      
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Add New Product</h1>
          <p className="mt-1 text-sm text-gray-500">
            Create a new product by following the steps below
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between">
            {STEPS.map((step) => (
              <div
                key={step.id}
                className={`flex flex-col items-center ${
                  currentStep >= step.id ? 'text-indigo-600' : 'text-gray-400'
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    currentStep >= step.id ? 'bg-indigo-600 text-white' : 'bg-gray-200'
                  }`}
                >
                  {step.id}
                </div>
                <span className="mt-2 text-sm">{step.title}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {renderStepContent()}
        </div>

        {/* Navigation Buttons */}
        <div className="mt-6 flex justify-between">
          {currentStep > 1 ? (
            <button
              onClick={handleBack}
              className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              <FiArrowLeft className="mr-2" />
              Back
            </button>
          ) : (
            <button
              onClick={handleCancel}
              className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-red-600 hover:bg-red-50"
            >
              <FiX className="mr-2" />
              Cancel
            </button>
          )}
          
          {currentStep < STEPS.length ? (
            <button
              onClick={handleNext}
              disabled={isSubmitting}
              className={`ml-auto flex items-center px-4 py-2 ${
                isSubmitting ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'
              } text-white rounded-md`}
            >
              {currentStep === 1 && isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating Product...
                </>
              ) : (
                <>
                  Next
                  <FiArrowRight className="ml-2" />
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleComplete}
              className="ml-auto flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md"
            >
              <FiCheck className="mr-2" />
              Complete
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default CreateProductWizard;

const handleMainInfoSubmit = async (mainInfoData) => {
  try {
    const productData = {
      name: mainInfoData.name,
      category_id: parseInt(mainInfoData.category_id),
      brand_id: parseInt(mainInfoData.brand_id),
      tag_ids: mainInfoData.tag_ids.map(id => parseInt(id)),
      price: mainInfoData.price,
      sale_price: mainInfoData.sale_price,
      description: mainInfoData.description,
      production_time: mainInfoData.production_time,
      shipping: mainInfoData.shipping,
      returns_policy: mainInfoData.returns_policy,
      is_new_arrival: mainInfoData.is_new_arrival || false,
      is_featured: mainInfoData.is_featured || false
    };

    const response = await api.createProduct(productData);
    // Handle successful creation
    toast.success('Product created successfully');
    // You might want to store the product ID for the next steps
    setProductId(response.id);
    // Move to next step in wizard
    setCurrentStep(currentStep + 1);
  } catch (error) {
    toast.error(error.message || 'Failed to create product');
    console.error('Error creating product:', error);
  }
};

const generateSKU = (size, color) => {
  const prefix = 'DYX';
  const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  const sizeCode = size.replace(/\s+/g, '').substring(0, 2).toUpperCase();
  const colorCode = color.replace(/\s+/g, '').substring(0, 2).toUpperCase();
  return `${prefix}${sizeCode}${colorCode}${randomNum}`;
};

const handleSubmit = async () => {
  try {
    // Show loading toast
    const loadingToast = toast.loading('Submitting product...');

    // Prepare final product data
    const finalProductData = {
      ...basicDetails,
      variants: variants.map(variant => ({
        ...variant,
        images: variantImages[variant.id] || []
      })),
      additional_info: additionalInfo
    };

    // Submit to API
    await api.submitProduct(finalProductData);

    // Success
    toast.dismiss(loadingToast);
    toast.success('Product created successfully');
    navigate('/admin/products');

  } catch (error) {
    toast.error(error.message || 'Failed to create product');
    console.error('Error submitting product:', error);
  }
};




